import { customElement, attachShadowTemplate, coerceNumber, coreProperty } from '@tylertech/forge-core';
import { BaseComponent, IBaseComponent } from '@tylertech/forge';
import { FooterCore } from './footer-core';
import { FooterAdapter } from './footer-adapter';
import { FooterLayout, FOOTER_CONSTANTS } from './footer-constants';
import { FooterItemComponent } from '../footer-item';

const template = '<template><div class=\"forge-footer\" part=\"root\"><div class=\"forge-footer__content\" role=\"list\" part=\"content\"><slot></slot></div><slot name=\"graphic\"><a href=\"https://www.tylertech.com/\" target=\"_blank\"><img src=\"https://cdn.forge.tylertech.com/v1/images/branding/tyler/tyler-empowered-logo-white.svg\" alt=\"Empowered by Tyler Technologies\" part=\"graphic\"></a></slot></div></template>';
const styles = '.forge-footer{background-color:var(--forge-footer-theme-background,#424242);padding:var(--forge-footer-padding,24px);padding-left:var(--forge-footer-padding-left,24px);padding-right:var(--forge-footer-padding-right,24px);gap:var(--forge-footer-gap,48px);display:grid;grid-template-columns:1fr auto;-webkit-box-align:center;align-items:center}.forge-footer__content{display:-webkit-box;display:flex;flex-wrap:wrap;-webkit-box-align:center;align-items:center;font-size:0}@supports not (grid-area:auto){.forge-footer__content{margin-right:var(--forge-footer-gap,48px)}}.forge-footer--alternative{gap:var(--forge-footer-alternative-gap,32px);grid-template-columns:1fr;grid-auto-flow:row;justify-items:center;text-align:center}.forge-footer--alternative .forge-footer__content{-webkit-box-pack:center;justify-content:center}@supports not (grid-area:auto){.forge-footer--alternative .forge-footer__content{margin-bottom:var(--forge-footer-alternative-gap,32px);margin-right:0}}.forge-footer ::slotted(:not(:last-child))::after{-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;font-family:var(--forge-typography-body2-font-family, var(--forge-typography-font-family, \"Roboto\", sans-serif));font-size:var(--forge-typography-body2-font-size, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-body-font-size-scale, 1)));font-weight:var(--forge-typography-body2-font-weight,400);line-height:var(--forge-typography-body2-line-height, calc(var(--forge-typography-font-size, 1rem) * var(--forge-typography-body-line-height-scale, 1.375)));letter-spacing:var(--forge-typography-body2-letter-spacing, .015625em);text-transform:var(--forge-typography-body2-text-transform,inherit);-webkit-text-decoration:var(--forge-typography-body2-text-decoration,inherit);text-decoration:var(--forge-typography-body2-text-decoration,inherit);color:var(--forge-footer-theme-on-background,#fff);line-height:1.375rem;margin-left:var(--forge-footer-divider-margin,12px);margin-right:var(--forge-footer-divider-margin,12px);content:\"|\";content:\"|\"/\"\"}:host{display:block;-webkit-box-sizing:border-box;box-sizing:border-box}:host([hidden]){display:none}';

export interface IFooterComponent extends IBaseComponent {
  layout: FooterLayout;
  layoutBreakpoint: number;
}

declare global {
  interface HTMLElementTagNameMap {
    'forge-footer': IFooterComponent;
  }
}
/**
 * A layout component that provides a consistent footer to use at the application level. It provides basic default
 * styling, as well as a built-in Tyler logo with link to Tyler's homepage, but otherwise you can use the default slot to provide any custom content
 * (typically in the form of links or static text).
 * 
 * @tag forge-footer
 */
@customElement({
  name: FOOTER_CONSTANTS.elementName,
  dependencies: [FooterItemComponent]
})
export class FooterComponent extends BaseComponent implements IFooterComponent {
  public static get observedAttributes(): string[] {
    return [
      FOOTER_CONSTANTS.attributes.LAYOUT,
      FOOTER_CONSTANTS.attributes.LAYOUT_BREAKPOINT
    ];
  }

  private _core: FooterCore;

  constructor() {
    super();
    attachShadowTemplate(this, template, styles);
    this._core = new FooterCore(new FooterAdapter(this));
  }

  public connectedCallback(): void {
    this._core.initialize();
  }

  public disconnectedCallback(): void {
    this._core.disconnect();
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    switch (name) {
      case FOOTER_CONSTANTS.attributes.LAYOUT:
        this.layout = newValue as FooterLayout;
        break;
      case FOOTER_CONSTANTS.attributes.LAYOUT_BREAKPOINT:
        this.layoutBreakpoint = coerceNumber(newValue);
        break;
    }
  }

  /** Gets/sets the layout mode */
  @coreProperty()
  public layout: FooterLayout;

  /** Gets/sets the breakpoint for automatic layout switching */
  @coreProperty()
  public layoutBreakpoint: number;
}
